import { useCallback, useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { CreditCard, Loader2, Repeat, Info, BellRing } from "lucide-react";

export default function MakePaymentPage() {
  const [types, setTypes] = useState([]);
  const [type, setType] = useState("monthly_dues");
  const [amount, setAmount] = useState(420);
  const [memo, setMemo] = useState("");
  const [loading, setLoading] = useState(false);
  const [autopay, setAutopay] = useState(null); // {active, subscription?}
  const [autopayLoading, setAutopayLoading] = useState(false);
  const [autopayAmount, setAutopayAmount] = useState(420);

  const refreshAutopay = useCallback(async () => {
    try {
      const { data } = await api.get("/payments/autopay/status");
      setAutopay(data);
    } catch (err) { if (typeof console !== 'undefined') console.debug('pages/resident/MakePaymentPage.jsx: swallowed', err); }
  }, []);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/payment-types");
      setTypes(data.items);
    })();
    refreshAutopay();
  }, [refreshAutopay]);

  const onSelectType = (v) => {
    setType(v);
    const t = types.find((x) => x.id === v);
    if (t) setAmount(t.default_amount);
  };

  const submit = async () => {
    if (!amount || Number(amount) <= 0) return toast.error("Enter a valid amount");
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        payment_type: type,
        amount: Number(amount),
        origin_url: window.location.origin,
        memo,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiErrorMessage(e));
      setLoading(false);
    }
  };

  const setupAutopay = async () => {
    if (!autopayAmount || Number(autopayAmount) <= 0) return toast.error("Enter a valid amount");
    setAutopayLoading(true);
    try {
      const { data } = await api.post("/payments/autopay/setup", {
        amount: Number(autopayAmount),
        origin_url: window.location.origin,
        label: "Monthly HOA Dues (Autopay)",
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiErrorMessage(e));
      setAutopayLoading(false);
    }
  };

  const cancelAutopay = async () => {
    setAutopayLoading(true);
    try {
      await api.post("/payments/autopay/cancel");
      toast.success("Autopay will end at the end of your current period.");
      refreshAutopay();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
    setAutopayLoading(false);
  };

  const sub = autopay?.subscription;

  return (
    <div>
      <PageHeader eyebrow="Payments" title="Make a payment" subtitle="Secure checkout via Stripe. Test card 4242 4242 4242 4242." />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <Card className="p-4 sm:p-6 bg-white">
          <div className="overline mb-3">One-time payment</div>
          <div className="space-y-4">
            <div>
              <Label>Payment type</Label>
              <Select value={type} onValueChange={onSelectType}>
                <SelectTrigger data-testid="pay-type" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {types.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Amount (USD)</Label>
              <Input data-testid="pay-amount" type="number" min="1" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1 tabular-nums" />
            </div>
            <div>
              <Label>Memo (optional)</Label>
              <Textarea data-testid="pay-memo" value={memo} onChange={(e) => setMemo(e.target.value)} placeholder="e.g. Feb dues, unit 12B" rows={2} className="mt-1" />
            </div>
            <div className="rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-xs p-3 flex items-start gap-2">
              <Info className="h-4 w-4 shrink-0 mt-0.5" />
              <div>Payments over <span className="font-medium">$5,000</span> route through admin approval before finalizing.</div>
            </div>
            <Button
              data-testid="pay-submit"
              onClick={submit}
              disabled={loading}
              className="w-full h-11 rounded-full bg-zinc-900 hover:bg-zinc-800 text-white"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CreditCard className="h-4 w-4 mr-2" /> Continue to secure checkout</>}
            </Button>
          </div>
        </Card>

        <Card className="p-4 sm:p-6 bg-gradient-to-br from-indigo-50 to-white border-indigo-100">
          <div className="flex items-center gap-2 mb-3">
            <Repeat className="h-4 w-4 text-indigo-600" />
            <div className="overline text-indigo-700">Autopay · Monthly HOA Dues</div>
          </div>
          {autopay?.active && sub ? (
            <>
              <div className="text-xl sm:text-2xl font-semibold font-display tabular-nums break-all">
                ${Number(sub.amount || 0).toFixed(2)} <span className="text-sm text-zinc-500 font-normal">/ month</span>
              </div>
              <Badge className="mt-2 bg-emerald-100 text-emerald-800 hover:bg-emerald-100 capitalize">
                {sub.cancel_at_period_end ? "canceling at period end" : sub.status}
              </Badge>
              {sub.current_period_end && (
                <div className="text-xs text-zinc-500 mt-3">
                  Next charge: <span className="text-zinc-800 font-medium">{new Date(sub.current_period_end).toLocaleDateString()}</span>
                </div>
              )}
              {sub.next_reminder_at && (
                <div className="mt-3 rounded-md bg-white/70 border border-indigo-100 p-3 text-xs text-indigo-800 flex items-start gap-2">
                  <BellRing className="h-4 w-4 shrink-0 mt-0.5" />
                  <span>You&apos;ll get a heads-up email on <b>{new Date(sub.next_reminder_at).toLocaleDateString()}</b> — 3 days before every charge.</span>
                </div>
              )}
              <p className="text-sm text-zinc-600 mt-4">
                You&apos;re on autopay. We&apos;ll charge your saved card every month — no more due-date reminders.
              </p>
              {!sub.cancel_at_period_end && (
                <Button data-testid="autopay-cancel" onClick={cancelAutopay} disabled={autopayLoading}
                        variant="outline" className="mt-4 rounded-full border-rose-300 text-rose-700 hover:bg-rose-50">
                  {autopayLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Cancel autopay"}
                </Button>
              )}
            </>
          ) : (
            <>
              <p className="text-sm text-zinc-600">
                Never miss a due date — enroll once and we&apos;ll charge your saved card every month. Cancel anytime.
              </p>
              <div className="mt-4">
                <Label>Monthly amount (USD)</Label>
                <Input data-testid="autopay-amount" type="number" min="1" step="0.01"
                       value={autopayAmount} onChange={(e) => setAutopayAmount(e.target.value)}
                       className="mt-1 tabular-nums" />
              </div>
              <Button data-testid="autopay-setup" onClick={setupAutopay} disabled={autopayLoading}
                      className="mt-4 w-full h-11 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white">
                {autopayLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Repeat className="h-4 w-4 mr-2" />Turn on autopay</>}
              </Button>
              <div className="text-[11px] text-zinc-500 mt-2">
                Powered by Stripe subscriptions. Test card: 4242 4242 4242 4242.
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
